import { createApi } from "@reduxjs/toolkit/query/react";
import { IRevenueData } from "../components/CustomerView/ConsumptionRevenueTab/ConsumptionRevenueTab.interfaces";
import { ProductTelemetryMonthSnapshot } from "../components/CustomerView/CustomerReportPdf";
import { ContactDataGroup, IContactData } from "../SharedComponents/Interfaces/CustomContact.interfaces";
import { IProductFamily, ISentinelDrilldownDataResponse, ITenantMdcProductFamilyLineChartData } from "../interfaces/customer-product-v2.interfaces";
import { ISelectedTenant, ITenantsMdcProductKPIsRequest } from "../interfaces/Customer.interfaces";
import { store } from "../store/store";
import { IPaginatedEvidenceRequest, IPaginatedFeatureRequestResponse } from "../interfaces/CloseTheLoop.interfaces";
import { baseQueryWithReauth } from "../Utils/baseQueryWithReauth";
import { chunkArray, groupBy } from "../Utils/js-common";
import { selectGraphToken } from "./Auth.slice";

// AI Insights data structure interface
export interface AIInsightData {
  tpid?: string;
  tenantId?: string;
  metricId?: string;
  metricName?: string;
  paragraph?: string;
  highlight1Title?: string;
  highlight1Description?: string;
  highlight2Title?: string;
  highlight2Description?: string;
  highlight3Title?: string;
  highlight3Description?: string;
  highlight4Title?: string;
  highlight4Description?: string;
  highlight5Title?: string;
  highlight5Description?: string;
  insightsGenerationDate?: string;
  insightsExpiryDate?: string;
}

// Interface for the new TPID Description and Logo API response
export interface TpidDescriptionAndLogoResponse {
  description?: string;
  logoInBase64Format?: string;
}

export interface SupportCaseAISummaryProps {
  caseNumber: any;
  derivedProductName: string;
  caseTitle: string;
  caseSummary: string;
  surveyRating: any;
  overallSentiment: string;
  processImprovement: string;
  productImprovement: string;
  documentationImprovement: string;
  toolImprovement: string;
  insertDate: string;
}

// Generic transform function that works with any contact-like data type
export const transformContactListResponse = async <
  T extends { emailAddress?: string; imageSrc?: string },
  R = ContactDataGroup
>(
  response: T[],
  groupByKey: string
): Promise<R[]> => {
  const state = store.getState();
  const token = selectGraphToken(state);

  const contacts = response.filter((contact) => contact?.emailAddress) ?? [];

  if (contacts.length === 0) {
    const family = groupBy(response, groupByKey);
    return Object.keys(family).map(
      (key: string): R =>
        ({
          categoryName: key === "null" ? null : key,
          data: family[key]
        }) as R
    );
  }

  // Build a Map keyed by email to avoid duplicates and eliminate undefined results
  const contactByEmail = new Map<string, T>();
  contacts.forEach((contact) => {
    if (contact.emailAddress) {
      contactByEmail.set(contact.emailAddress, contact);
    }
  });

  const uniqueContacts = Array.from(contactByEmail.values());
  const splitContacts = chunkArray(uniqueContacts, 20);
  const imageByEmail = new Map<string, string>();

  // Process each chunk of 20 contacts
  for (const contactChunk of splitContacts) {
    const graphBody = {
      requests: contactChunk.map((contact) => ({
        id: contact.emailAddress,
        method: "GET",
        url: `/users/${encodeURIComponent(contact.emailAddress ?? "")}/photos/48x48/$value`
      }))
    };

    try {
      const data = await fetch("https://graph.microsoft.com/v1.0/$batch", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(graphBody)
      });

      if (data.status === 200) {
        const batchJson = await data.json();

        if (batchJson?.responses && Array.isArray(batchJson.responses)) {
          batchJson.responses.forEach((item: { id?: string; status?: number; body?: string }) => {
            if (item?.status === 200 && typeof item.body === "string" && item.id) {
              imageByEmail.set(item.id, `data:image/jpeg;base64,${item.body}`);
            }
          });
        }
      }
    } catch (error) {
      // Continue processing remaining chunks even if one fails
      continue;
    }
  }

  const updatedResponse = response.map((contact) => ({
    ...contact,
    imageSrc:
      (contact?.emailAddress ? imageByEmail.get(contact.emailAddress) : undefined) ??
      (contact.imageSrc ? `data:image/jpeg;base64,${contact.imageSrc}` : undefined)
  }));

  const family = groupBy(updatedResponse, groupByKey);

  return Object.keys(family).map(
    (key: string): R =>
      ({
        categoryName: key === "null" ? null : key,
        data: family[key]
      }) as R
  );
};

// Define a service using a base URL and expected endpoints
export const explorerCustomersAPI = createApi({
  reducerPath: "explorerCustomers",
  tagTypes: ["FavoritesCustomer"],
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    getProductKPIs: builder.query<IProductFamily[], string>({
      query: (id) => `customer/getFactProductKPIByTenantId?TenantId=${id}`,
      // Cache product KPIs for 30 minutes to improve performance on tab switches
      keepUnusedDataFor: 1800
    }),
    getProductKPIsMonthSnapshot: builder.query<ProductTelemetryMonthSnapshot[], string>({
      query: (id) => `customer/getTenantProfileWithProductMetricsMonthSnapshot?TenantId=${id}`
    }),
    getTpidDescriptionAndLogo: builder.query<
      TpidDescriptionAndLogoResponse,
      { tpid: string; tenantId: string; tenantName: string }
    >({
      query: ({ tpid, tenantId, tenantName }) =>
        `customer/getTpidDescriptionAndLogo?tpid=${tpid}&tenantId=${tenantId}&tenantName=${encodeURIComponent(tenantName)}`
    }),
    getEngagements: builder.query({
      query: (id) => `customer/getEngagementProgramData?TenantId=${id}`
    }),
    getTenantSupportDetails: builder.query({
      query: (id) => `customer/getSupportDataOfTenant?TenantId=${id}`
    }),
    getPartnerData: builder.query({
      // Accept an array: [tenantId, workloadType]
      query: ([tenantId, workloadType]: [string, string]) =>
        `customer/getPartnerData?TenantId=${tenantId}&WorkLoadType=${workloadType}`
    }),
    getFeaturesData: builder.query({
      query: ({ id, productFamily }: { id: any; productFamily: any }) =>
        `customer/getFeatureData?TenantId=${id}&ProductFamily=${productFamily}`
    }),
    getFeatureDataForSecurityCopilot: builder.query({
      query: ({ id }: { id: any }) => `customer/getFeatureDataForSecurityCopilot?TenantId=${id}`
    }),
    getSentinelDrilldownData: builder.query<
      ISentinelDrilldownDataResponse,
      { tenantId: string }
    >({
      query: ({ tenantId }) => `customer/getSentinelDrilldownData?tenantId=${tenantId}`,
      // Cache Sentinel data for 30 minutes to improve performance on tab switches
      keepUnusedDataFor: 1800
    }),
    getCustomerEngagements: builder.query({
      query: (id) => `customer/getCustomerEngagements?TenantId=${id}`
    }),
    GetTenantData: builder.query<ISelectedTenant[], string | null>({
      query: (id) => `customer/getTenantData?TenantId=${id}`
    }),
    getFavorableCustomerByLogin: builder.query({
      query: () => "customer/getFavCustomersByLogin",
      providesTags: ["FavoritesCustomer"]
    }),
    getTenantList: builder.mutation({
      query(body) {
        return {
          url: "customer/getTenantsList",
          method: "POST",
          body
        };
      }
    }),
    insertUpdateFavCustomers: builder.mutation({
      query(customer) {
        return {
          url: "customer/insertUpdateFavCustomers",
          method: "POST",
          body: customer
        };
      },
      invalidatesTags: ["FavoritesCustomer"]
    }),
    getAIInsightData: builder.query<AIInsightData[], { metricId?: string; tenantId?: string }>({
      query: ({ metricId, tenantId }) => ({
        url: `customer/getAIInsightData?metricId=${metricId}&tenantId=${tenantId}`
      })
    }),
    getMissingProductsInsightData: builder.query<string, { tenantId?: string }>({
      query: ({ tenantId }) => ({
        url: `customer/getMissingProductsInsightData?TenantId=${tenantId}`
      })
    }),
    getSupportCaseAISummary: builder.query<SupportCaseAISummaryProps, number>({
      query: (caseNumber) => `customer/getSupportAISummaryByCaseNumber?caseNumber=${caseNumber}`
    }),
    // This mutation inserts a recently viewed customer record.
    // Takes an object with LoginName, LoginEmail, TenantId, and TenantName as input.
    // Sends a POST request to "customer/insertRecentlyViewedCustomer" with the input data.
    // Returns a boolean to indicate success or failure.
    insertRecentlyViewedCustomer: builder.mutation<
      boolean,
      {
        LoginName: string;
        LoginEmail: string;
        TenantId: string;
        TenantName: string;
      }
    >({
      query(body) {
        return {
          url: "customer/insertRecentlyViewedCustomer",
          method: "POST",
          body
        };
      }
    }),
    getCustomerHealthScore: builder.query({
      query: (tenantId) => `customer/getCustomerHealthScore?TenantId=${tenantId}`
    }),
    getTenantACRDetails: builder.query<IRevenueData, { topParentId: string; serviceFamily: string }>({
      query: ({ topParentId, serviceFamily }) => ({
        url: `customer/getTenantACRDetails?TopParentId=${topParentId}&ServiceFamily=${serviceFamily}`,
        headers: {
          ktk: sessionStorage.getItem("kqlToken") ?? undefined
        }
      })
    }),
    getCommunityDetails: builder.query({
      query: (id) => `customer/getCommunityDetails?tenantId=${id}`
    }),
    getTenantsMdcProductKPIs: builder.query<ITenantMdcProductFamilyLineChartData[], ITenantsMdcProductKPIsRequest>({
      // Keep data for 30 minutes (increased from 10 minutes to better prevent duplicate API calls)
      keepUnusedDataFor: 1800,
      query({ tenantIds, numberOfMonths, numberOfKPIs }) {
        const baseUrl = "customer/getTenantsMdcProductTelemetryMetricsData";
        const params = new URLSearchParams();

        tenantIds.forEach((id) => params.append("tenantIds", id));
        if (numberOfMonths !== undefined) params.append("numberOfMonths", numberOfMonths.toString());
        if (numberOfKPIs !== undefined) params.append("numberOfKPIs", numberOfKPIs.toString());

        return `${baseUrl}?${params.toString()}`;
      }
    }),
    getDomainDetails: builder.query({
      query: (id) => `customer/getDomainDetails?TenantId=${id}`
    }),
    getCustomerContactList: builder.query<ContactDataGroup[], string>({
      query: (id: string) => `customer/getCustomerContactList?TenantId=${id}`,
      transformResponse: async (response: IContactData[]) => await transformContactListResponse(response, "title")
    }),
    getCustomerManualContactList: builder.query<ContactDataGroup[], string>({
      query: (id: string) => `customer/getCustomerManualContactList?TenantId=${id}`,
      transformResponse: async (response: IContactData[]) => await transformContactListResponse(response, "productName")
    }),
    getCustomerCxEContactList: builder.query<ContactDataGroup[], string>({
      query: (id: string) => `customer/getCustomerCxEContactList?TenantId=${id}`,
      transformResponse: async (response: IContactData[]) => await transformContactListResponse(response, "productName")
    }),
    getCSAMContactList: builder.query<ContactDataGroup[], string>({
      query: (tpid: string) => `customer/getCSAMContactList?tpid=${tpid}`,
      transformResponse: async (response: IContactData[]) => {
        const result = await transformContactListResponse(response, "nameRole");
        // Filter out BackupCSAM and sort: PrimaryCSAM first, then PrimaryCSAMManager
        const sortOrder: Record<string, number> = {
          PrimaryCSAM: 1,
          PrimaryCSAMManager: 2
        };
        return result
          .filter((group) => group.categoryName !== "BackupCSAM")
          .sort((a, b) => {
            const orderA = sortOrder[a.categoryName || ""] ?? 99;
            const orderB = sortOrder[b.categoryName || ""] ?? 99;
            return orderA - orderB;
          });
      }
    }),
    getEngagementContacts: builder.query<ContactDataGroup[], string>({
      query: (tenantId: string) => `customer/getEngagementContacts?tenantId=${tenantId}`,
      transformResponse: async (response: IContactData[]) => {
        const result = await transformContactListResponse(response, "nameRole");
        // Sort: Account Manager first, then Specialists
        const sortOrder: Record<string, number> = {
          "Specialist Sales": 1,
          "Solution Area Specialists M": 2,
          "Solution Area Specialists IC": 3
        };
        return result.sort((a, b) => {
          const orderA = sortOrder[a.categoryName || ""] ?? 99;
          const orderB = sortOrder[b.categoryName || ""] ?? 99;
          return orderA - orderB;
        });
      }
    }),
    getSubscriptionByTenantId: builder.query({
      // Accepts [tenantId, skuType] as argument
      query: ([tenantId, skuType]: [string, string]) =>
        `customer/getSubscriptionByTenant?TenantId=${tenantId}&SkuType=${skuType}`
    }),
    getCRMFeatureRequestData: builder.query<
      IPaginatedFeatureRequestResponse,
      { tenantId: string } & Partial<IPaginatedEvidenceRequest>
    >({
      query: ({ tenantId, page = 1, pageSize = 1000000, sortBy = "lastChangedDate", sortDirection = "desc" }) => {
        const searchParams = new URLSearchParams();
        searchParams.append("TenantId", tenantId);
        searchParams.append("page", page.toString());
        searchParams.append("pageSize", pageSize.toString());
        if (sortBy) searchParams.append("sortBy", sortBy);
        if (sortDirection) searchParams.append("sortDirection", sortDirection);
        return `customer/getCRMFeatureRequestData?${searchParams.toString()}`;
      }
    }),
    getCRMFeatureRequestDataByTPID: builder.query<
      IPaginatedFeatureRequestResponse,
      { tpid: string } & Partial<IPaginatedEvidenceRequest>
    >({
      query: ({ tpid, page = 1, pageSize = 1000000, sortBy = "lastChangedDate", sortDirection = "desc" }) => {
        const searchParams = new URLSearchParams();
        searchParams.append("tpid", tpid);
        searchParams.append("page", page.toString());
        searchParams.append("pageSize", pageSize.toString());
        if (sortBy) searchParams.append("sortBy", sortBy);
        if (sortDirection) searchParams.append("sortDirection", sortDirection);
        return `customer/getCRMFeatureRequestDataByTPID?${searchParams.toString()}`;
      }
    }),
    getCRMCustomerProductForTenant: builder.query({
      query: (id) => `customer/getCRMCustomerProductForTenant?TenantId=${id}`
    }),
    getCRMEngagementAISummaryByCustomerProductId: builder.query<string, { customerProductId: string }>({
      query: ({ customerProductId }) => ({
        url: `customer/getCRMEngagementAISummaryByCustomerProductId?customerProductId=${customerProductId}`
      })
    }),
    getRFACustomerProductForTenant: builder.query({
      query: (id) => `customer/getRFACustomerProductForTenant?TenantId=${id}`
    }),
    getCRMRequestAssistanceData: builder.query({
      query: (id) => `customer/getCRMRequestAssistanceData?TenantId=${id}`
    })
  })
});

// Export hooks for usage in functional components, which are
// Auto-generated based on the defined endpoints
export const {
  useLazyGetSupportCaseAISummaryQuery,
  useLazyGetAIInsightDataQuery,
  useGetProductKPIsQuery,
  useLazyGetProductKPIsQuery,
  useGetTpidDescriptionAndLogoQuery,
  useGetEngagementsQuery,
  useGetTenantListMutation,
  useGetTenantDataQuery,
  useInsertUpdateFavCustomersMutation,
  useGetFavorableCustomerByLoginQuery,
  useGetTenantSupportDetailsQuery,
  useLazyGetTenantSupportDetailsQuery,
  useGetPartnerDataQuery,
  useGetFeaturesDataQuery,
  useGetFeatureDataForSecurityCopilotQuery,
  useGetCustomerEngagementsQuery,
  useGetCustomerHealthScoreQuery,
  useLazyGetMissingProductsInsightDataQuery,
  useLazyGetProductKPIsMonthSnapshotQuery,
  useInsertRecentlyViewedCustomerMutation,
  useGetTenantACRDetailsQuery,
  useGetCommunityDetailsQuery,
  useGetTenantsMdcProductKPIsQuery,
  useGetDomainDetailsQuery,
  useGetCustomerContactListQuery,
  useGetCustomerCxEContactListQuery,
  useGetCustomerManualContactListQuery,
  useGetCSAMContactListQuery,
  useGetEngagementContactsQuery,
  useGetSubscriptionByTenantIdQuery,
  useGetCRMFeatureRequestDataQuery,
  useGetCRMFeatureRequestDataByTPIDQuery,
  useGetCRMCustomerProductForTenantQuery,
  useLazyGetCRMEngagementAISummaryByCustomerProductIdQuery,
  useGetRFACustomerProductForTenantQuery,
  useGetCRMRequestAssistanceDataQuery,
  useGetSentinelDrilldownDataQuery,
  useLazyGetSentinelDrilldownDataQuery
} = explorerCustomersAPI;
